import { LitElement, type PropertyValues, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-chat.css?inline'
import {
  asContactDetails,
  asExtras,
  asFulfillment,
  asModifiers,
  asQuantityConfirm,
  asScheduledTime,
  asSubmit,
  asSuggestions,
  isBlocking,
} from './bbcore/hints'
import type { ChatMessage } from './bbcore/conversation'
import type {
  ContactDetailsInputHint,
  ExtrasChoiceHint,
  FulfillmentInputHint,
  Hint,
  ItemsSuggestionHint,
  ModifierChoiceHint,
  QuantityConfirmHint,
  ScheduledTimeConfirmHint,
} from './bbcore/types'
import './bb-menu-item'
import './bb-chat-modifier'
import './bb-chat-contact'
import './bb-chat-fulfillment'
import './bb-chat-scheduled-time'

// Match bb-menu's canonical `R36.00` format so the chat suggestion
// chip and the menu tile speak the same currency.
function formatPriceCents(cents: number | null | undefined): string {
  if (cents == null) return 'R0.00'
  return `R${(cents / 100).toFixed(2)}`
}
import type { ModifierConfirmation } from './bb-chat-modifier'

// Presentational. The conversation lives in `<bb-widget>`'s
// ConversationController; this panel renders the transcript and pushes intent up
// via bubbled CustomEvents:
//   - `send-utterance` { text }                — composer / suggestion chips / modifier picker
//   - `set-fulfillment` { fulfillmentType, address? } — Pickup / Delivery affordance
//   - `submit-order`                            — "Place my order" affordance (the
//                                                 widget calls `controller.submitOrder()`
//                                                 directly, same path the cart button
//                                                 takes — bypasses the LLM so it can't
//                                                 stall on the review_order/submit_order
//                                                 two-step)
//   - `fetch-sample-address` { fill(address) } — "use a sample address" button (the
//                                                 handler calls `fill` with the result)
//
//   - `renderHints` (default true): each bot message's hint also renders as an inline
//     affordance below its bubble (Pickup/Delivery — Delivery expands to an address
//     field; modifier picker; suggestion chips; a "Place my order" confirm button).
//     Past affordances stay in the transcript, disabled, once a newer turn supersedes
//     them; only the affordance on the last message is live.
//   - `renderHints=false`: no hint affordances; the user types
//     responses to structured prompts. (The raw hint is still visible in the
//     `log` panel.)
//
// `readonly` hides the composer and disables affordances (look-only view).

@customElement('bb-chat')
export class BbChat extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false })
  messages: ChatMessage[] = []

  @property({ attribute: false })
  error: string | null = null

  @property({ attribute: false })
  busy = false

  // When true (default), bot hints render as inline affordances (one tap = the
  // response). When false, no hint UI is drawn — the user types every response.
  @property({ attribute: false })
  renderHints = true

  // When true, the chat panel renders the fulfillment hint as nothing —
  // the host (bb-widget) is rendering it elsewhere (typically as a
  // visually-modal overlay).
  @property({ attribute: false })
  suppressFulfillment = false

  // When true, the chat panel renders the submit hint's affordance as
  // nothing — host is rendering the confirm CTA elsewhere (overlay).
  @property({ attribute: false })
  suppressSubmit = false

  // When true, the chat panel renders the contact_details hint as
  // nothing — host (bb-widget) is rendering it elsewhere (overlay).
  @property({ attribute: false })
  suppressContact = false

  // When true, the chat panel renders the scheduled_time hint as
  // nothing — host (bb-widget) is rendering it elsewhere (overlay).
  @property({ attribute: false })
  suppressScheduling = false

  // Hides the composer and disables affordances (look-only view). Not used by
  // `<bb-widget>` today.
  @property({ type: Boolean, reflect: true })
  readonly = false

  @state() private draft = ''

  // Sticky-bottom scroll. `updated()` snaps on bb-chat property
  // changes (messages, busy, …), but child custom-elements grow the
  // transcript without triggering our update: bb-chat-fulfillment's
  // delivery sub-form opens, autocomplete suggestions appear, the
  // modifier picker lays out images. A ResizeObserver on #messages
  // catches all of those; the sticky flag avoids yanking the user
  // back to the bottom while they're scrolled up reading history.
  private stickToBottom = true
  private resizeObserver: ResizeObserver | null = null
  // True between a programmatic `scrollTop = scrollHeight` write and
  // the next frame. The browser delivers scroll events async, so if a
  // child custom element (modifier picker, fulfillment Delivery
  // expand) grows in the microtask between our snap and the scroll
  // event firing, the listener would read isNearBottom against the
  // grown viewport, see "user scrolled up", and flip stickToBottom
  // off — racing the ResizeObserver that would re-snap.
  private programmaticScroll = false

  disconnectedCallback() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    super.disconnectedCallback()
  }

  private get messagesEl(): HTMLElement | null {
    return this.renderRoot.querySelector<HTMLElement>('#messages')
  }

  private isNearBottom(el: HTMLElement): boolean {
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40
  }

  private snapToBottom() {
    const el = this.messagesEl
    if (!el) return
    this.programmaticScroll = true
    el.scrollTop = el.scrollHeight
    requestAnimationFrame(() => { this.programmaticScroll = false })
  }

  private onMessagesScroll = () => {
    if (this.programmaticScroll) return
    const el = this.messagesEl
    if (el) this.stickToBottom = this.isNearBottom(el)
  }

  private fire(text: string) {
    if (this.readonly || !text.trim()) return
    this.dispatchEvent(
      new CustomEvent('send-utterance', {
        detail: { text },
        bubbles: true,
        composed: true,
      }),
    )
    this.draft = ''
  }

  private onSubmit(e: Event) {
    e.preventDefault()
    this.fire(this.draft)
  }

  private onModifierConfirmed = (e: Event) => {
    // Route the picker confirm through the direct cart-add endpoint
    // (handled by the widget root) instead of firing an utterance through
    // the LLM. Going via the LLM let the agent's `ask_about_extras`
    // heuristic re-prompt for optional groups the customer had already
    // skipped in the picker (Trello #124 prod regression). The direct
    // path is the same one the menu's simple-item click uses; it writes
    // a `[widget action]` SystemMessage into LangGraph state so the LLM
    // stays aware of the add on subsequent turns.
    const detail = (e as CustomEvent).detail as ModifierConfirmation
    const options: string[] = []
    const extras: string[] = []
    const removals: string[] = []
    // Route per-option using each option's modifier_type when available
    // (a single group can mix kinds — "Add Mayo Inside?" is groupType
    // 'adjustments' but mayo is an addition). Fall back to group-level
    // routing when no per-option type was threaded through (legacy hints
    // synthesised without it, e.g. ones from the backend /cart/add 400
    // path that don't carry per-option types yet).
    for (const s of detail.selections) {
      if (s.options.length === 0) continue
      for (const name of s.options) {
        const optionType = s.optionTypes?.[name]
        if (optionType) {
          if (optionType === 'removal') removals.push(name)
          else if (optionType === 'addition') extras.push(name)
          else options.push(name)
        } else if (s.groupType === 'removal') {
          removals.push(name)
        } else if (s.minSelections === 0) {
          extras.push(name)
        } else {
          options.push(name)
        }
      }
    }
    this.dispatchEvent(
      new CustomEvent('modifier-add', {
        detail: {
          itemName: detail.itemName,
          quantity: detail.quantity,
          ...(detail.price ? { price: detail.price } : {}),
          options,
          extras,
          removals,
        },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private onModifierCancelled = () => {
    this.fire('Never mind, cancel that.')
  }

  // Render the affordance for one message's hint. `active` ⇒ it's the hint on
  // the last message and is live; otherwise it's a past hint, shown disabled.
  //
  // Hard rule for non-blocking hints (suggestions): chips render exactly
  // where they arrived and never move. Multiple chip blocks accumulate
  // through the transcript — one per recommendation turn — and each
  // stays clickable forever. The backend only emits a suggestions hint
  // when the customer asked, so this can't pollute with unsolicited
  // chips; once emitted, the chips are theirs to keep.
  private renderHint(hint: Hint | null | undefined, active: boolean) {
    if (!hint || !this.renderHints) return nothing
    const off = this.readonly || !active // disabled: look-only, or a superseded hint
    const mods = asModifiers(hint)
    if (mods) return this.renderModifierPicker(mods, off)
    if (!isBlocking(hint)) {
      // Non-blocking + sticky chips: extras upsell (Trello #118) takes
      // precedence over generic search suggestions when both could
      // theoretically be present (server picks one per turn, but the
      // typed check keeps render order explicit).
      const ext = asExtras(hint)
      if (ext) return this.renderExtras(ext, this.readonly)
      const sug = asSuggestions(hint)
      if (!sug) return nothing
      // Non-blocking + sticky: always clickable, never removed,
      // anchored to the message they arrived with.
      return this.renderSuggestions(sug, this.readonly)
    }
    const ff = asFulfillment(hint)
    if (ff) {
      if (this.suppressFulfillment) return nothing
      return this.renderFulfillment(ff, off, active)
    }
    const cc = asContactDetails(hint)
    if (cc) {
      if (this.suppressContact) return nothing
      return this.renderContact(cc, off, active)
    }
    if (asSubmit(hint)) {
      if (this.suppressSubmit) return nothing
      return this.renderSubmit(off)
    }
    const qc = asQuantityConfirm(hint)
    if (qc) return this.renderQuantityConfirm(qc, off)
    const st = asScheduledTime(hint)
    if (st) {
      if (this.suppressScheduling) return nothing
      return this.renderScheduledTime(st, off, active)
    }
    return nothing
  }

  // Closed-store slot picker (Trello #87 / #109). The custom element
  // <bb-chat-scheduled-time> owns the form; the widget catches the
  // composed `schedule-order` / `schedule-decline` events and sends the
  // corresponding plain utterance through the agent. (Trello #131
  // extracted this from a private renderer so it could mount in an
  // overlay alongside fulfillment/contact/submit.)
  private renderScheduledTime(
    hint: ScheduledTimeConfirmHint,
    off: boolean,
    active: boolean,
  ) {
    return html`
      <div class="hint-card">
        <bb-chat-scheduled-time
          .hint=${hint}
          ?disabled=${off}
          .active=${active}
        ></bb-chat-scheduled-time>
      </div>
    `
  }

  private renderQuantityConfirm(hint: QuantityConfirmHint, off: boolean) {
    // Yes/No chips for the quantity-threshold or duplicate-add prompt.
    // Both send a normal utterance: "yes" → the agent retries
    // add_to_cart with confirmed=True; "no" → the agent acknowledges
    // and the pending add is dropped. We tag the affordance event
    // with the reason so the log panel can show which case fired.
    const reason = hint.context.reason
    return html`
      <div class="hint">
        <button ?disabled=${off} @click=${() => {
          this.emitAffordance('quantity-confirm-yes', { reason })
          this.fire('yes')
        }}>Yes</button>
        <button ?disabled=${off} @click=${() => {
          this.emitAffordance('quantity-confirm-no', { reason })
          this.fire('no')
        }}>No</button>
      </div>
    `
  }

  private renderModifierPicker(mods: ModifierChoiceHint, off: boolean) {
    return html`
      <div class="hint-card hint-card--modifier">
        <bb-chat-modifier
          .hint=${mods}
          ?disabled=${off}
          @modifier-confirmed=${this.onModifierConfirmed}
          @modifier-cancelled=${this.onModifierCancelled}
        ></bb-chat-modifier>
      </div>
    `
  }

  private renderExtras(ext: ExtrasChoiceHint, off: boolean) {
    // Non-blocking upsell chips for optional modifier groups on the
    // just-added item (Trello #117/#118). One chip per option across
    // all optional groups. Clicking sends a plain utterance asking
    // the agent to add the extra to that item — the text path the
    // voice/no-hints customer would have taken if the LLM had asked.
    const options = ext.groups.flatMap((g) =>
      g.options.map((o) => ({ ...o, group: g.name })),
    )
    if (options.length === 0) return nothing
    const itemName = ext.context.item_name
    return html`
      <div class="suggestions">
        ${options.map(
          (o) => html`<bb-menu-item
            size="sm"
            ?disabled=${off}
            .name=${o.name}
            .price=${formatPriceCents(o.price_cents)}
            @item-pick=${() => {
              this.emitAffordance('extras-chip', {
                item: itemName,
                option: o.name,
                group: o.group,
              })
              this.fire(`Please add ${o.name} to my ${itemName}.`)
            }}
          ></bb-menu-item>`,
        )}
      </div>
    `
  }

  private renderSuggestions(sug: ItemsSuggestionHint, off: boolean) {
    if (sug.items.length === 0) return nothing
    return html`
      <div class="suggestions">
        ${sug.items.slice(0, 5).map(
          (it) => html`<bb-menu-item
            size="sm"
            ?disabled=${off}
            .name=${it.name}
            .price=${formatPriceCents(it.price_cents)}
            .imageUrl=${it.image_url ?? null}
            @item-pick=${() => {
              this.emitAffordance('suggestion-chip', { item: it.name })
              this.fire(`I'll have the ${it.name}`)
            }}
          ></bb-menu-item>`,
        )}
      </div>
    `
  }

  private renderFulfillment(
    ff: FulfillmentInputHint,
    off: boolean,
    active: boolean,
  ) {
    return html`
      <div class="hint-card">
        <bb-chat-fulfillment
          .hint=${ff}
          ?disabled=${off}
          .active=${active}
        ></bb-chat-fulfillment>
      </div>
    `
  }

  private renderContact(
    cc: ContactDetailsInputHint,
    off: boolean,
    active: boolean,
  ) {
    return html`
      <div class="hint-card">
        <bb-chat-contact
          .hint=${cc}
          ?disabled=${off}
          .active=${active}
        ></bb-chat-contact>
      </div>
    `
  }

  private renderSubmit(off: boolean) {
    // A confirmation affordance — one tap fires the `submit-order` event the
    // widget handles by calling `controller.submitOrder()` directly, same as
    // the cart-side "Submit Order" button. We deliberately do NOT route this
    // through the LLM (no `fire('Yes, place my order')`): submit_order is a
    // two-step tool chain on the backend (review_order → submit_order) that
    // the LLM stalls on once fulfillment is already set, asking for confirmation
    // repeatedly or refusing outright. With fulfillment set, the deterministic
    // /orders/submit path always works and matches what the cart button does.
    return html`
      <div class="hint">
        <button ?disabled=${off} @click=${() => {
          this.emitAffordance('submit-inline')
          this.emitSubmitOrder()
        }}>
          Place my order
        </button>
      </div>
    `
  }

  private emitSubmitOrder() {
    this.dispatchEvent(
      new CustomEvent('submit-order', { bubbles: true, composed: true }),
    )
  }

  private emitAffordance(
    kind: string,
    context?: Record<string, unknown>,
  ) {
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind, ...(context ? { context } : {}) },
        bubbles: true,
        composed: true,
      }),
    )
  }

  render() {
    return html`
      <div class="header">
        <div class="dot">BB</div>
        <div class="title" part="chat-title">BiteBerry Chat</div>
      </div>
      <div class="messages" id="messages">
        ${this.messages.map((m, i) => {
          const isLast = i === this.messages.length - 1
          return html`
            <div class="msg ${m.who}">
              <div class="bubble">${m.text}</div>
            </div>
            ${m.who === 'bot' ? this.renderHint(m.hint, isLast) : nothing}
          `
        })}
        ${this.busy ? html`<div class="typing">Thinking…</div>` : nothing}
      </div>
      ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
      ${this.readonly
        ? nothing
        : html`
            <form class="input-row" @submit=${this.onSubmit}>
              <div class="input-shell">
                <input
                  type="text"
                  placeholder="Start typing..."
                  .value=${this.draft}
                  ?disabled=${this.busy}
                  @input=${(e: Event) =>
                    (this.draft = (e.target as HTMLInputElement).value)}
                />
                <button
                  type="submit"
                  ?disabled=${this.busy || !this.draft.trim()}
                  aria-label="Send"
                >
                  ${this.busy
                    ? html`<span
                          class="bb-btn-spinner"
                          part="btn-spinner"
                          aria-hidden="true"
                        ></span>`
                    : '➤'}
                </button>
              </div>
            </form>
          `}
    `
  }

  firstUpdated() {
    const el = this.messagesEl
    if (!el) return
    el.addEventListener('scroll', this.onMessagesScroll, { passive: true })
    // Watch the messages container — fires when *any* descendant
    // (hint affordance opening, suggestions list appearing, image
    // loading) changes its size. Snap to bottom only if the user was
    // already there.
    this.resizeObserver = new ResizeObserver(() => {
      if (this.stickToBottom) this.snapToBottom()
    })
    this.resizeObserver.observe(el)
    // Also observe each direct child so growth from layout of a
    // newly-appended message bubble / hint card is caught even when
    // the container's own height is constrained by flex.
    for (const child of el.children) this.resizeObserver.observe(child)
    this.snapToBottom()
  }

  updated(changed: PropertyValues<this>) {
    if (
      changed.has('messages') ||
      changed.has('renderHints') ||
      changed.has('busy')
    ) {
      const last = this.messages[this.messages.length - 1]
      const modifierLive =
        this.renderHints &&
        !this.busy &&
        last?.who === 'bot' &&
        !!asModifiers(last.hint)
      this.toggleAttribute('data-modifier-active', modifierLive)
    }
    if (changed.has('messages')) {
      // A new turn arrived; rewire the per-child observer to include
      // anything newly rendered (and drop any vanished children).
      const el = this.messagesEl
      if (el && this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver.observe(el)
        for (const child of el.children) this.resizeObserver.observe(child)
      }
      // New message ⇒ always snap, even if the user had scrolled up:
      // chatting is the dominant intent, "reading history mid-turn"
      // is rare enough that erring toward bottom is right.
      this.stickToBottom = true
    }
    if (this.stickToBottom) this.snapToBottom()
    // When a turn finishes (`busy` goes true → false) the composer's
    // `disabled` flips back off — but the browser already moved focus
    // away when it was disabled, so the user is left typing into the
    // void until they click back in. Re-focus it. Only on the
    // transition (not on a turn that started, not on first render —
    // grabbing focus on mount would be rude if the widget isn't the
    // page's main thing).
    //
    // The opening greeting is also a `busy` transition (`starting`
    // true → false), but it's bot-only and not user-initiated, so
    // refocusing there is the same "rude on mount" case — and worse
    // when embedded: focusing inside a cross-origin iframe scrolls the
    // *parent* page to the demo (preventScroll can't reach across the
    // frame). Gate on the transcript already having a user message so
    // the greeting never grabs focus; every real post-turn refocus is
    // unchanged.
    if (
      changed.has('busy') &&
      this.busy === false &&
      changed.get('busy') === true &&
      this.messages.some((m) => m.who === 'user')
    ) {
      this.renderRoot
        .querySelector<HTMLInputElement>('.input-row input')
        ?.focus({ preventScroll: true })
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-chat': BbChat
  }
}
